import { useQuery } from '@tanstack/react-query';
import { TrendingUp, ShoppingCart, AlertTriangle, Loader, BarChart3, Calendar, Package } from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth'; // 👈 NEW: Imported Auth Hook

const AdminHome = () => {
    const axiosSecure = UseAxiosSecure();
    const { activeBranchId } = UseAuth(); // 👈 NEW: Get active branch from global state

    // FETCH REAL STATS (Now Monthly Filtered by Backend)
    const { data: stats, isLoading } = useQuery({
        // 👈 NEW: Added activeBranchId to queryKey so it refetches when branch changes
        queryKey: ['admin-stats', activeBranchId], 
        queryFn: async () => {
            const res = await axiosSecure.get('/admin/stats');
            return res.data?.data;
        },
        // 👈 NEW: Prevent fetching until a branch is selected
        enabled: !!activeBranchId 
    });

    if (isLoading || !activeBranchId) {
        return (
            <div className="h-[60vh] flex flex-col items-center justify-center text-white">
                <Loader className="animate-spin mb-4 text-pink-500" size={48} />
                <p>{!activeBranchId ? "Selecting branch..." : "Loading Dashboard..."}</p>
            </div>
        );
    }

    // 1. CARDS CONFIGURATION
    const statsCards = [
        { 
            label: 'Monthly Revenue', 
            value: `৳${(stats?.revenue || 0).toFixed(2)}`, 
            color: 'from-pink-500 to-rose-500', 
            icon: <span className="text-xl font-bold">৳</span> 
        },
        { 
            label: 'Monthly Profit', 
            value: `৳${(stats?.profit || 0).toFixed(2)}`, 
            color: 'from-emerald-500 to-teal-500', 
            icon: <TrendingUp size={20}/> 
        },
        { 
            label: 'Monthly Orders', 
            value: stats?.orders || 0, 
            color: 'from-purple-500 to-indigo-500', 
            icon: <ShoppingCart size={20}/> 
        },
        { 
            label: 'Low Stock Alerts', 
            value: stats?.lowStockCount || 0, 
            color: (stats?.lowStockCount || 0) > 0 ? 'from-orange-500 to-red-500' : 'from-blue-500 to-cyan-500', 
            icon: <AlertTriangle size={20}/>,
            isAlert: (stats?.lowStockCount || 0) > 0
        },
    ];

    // 2. GRAPH DATA PREPARATION (Last 7 Days)
    const getLast7Days = () => {
        const days = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0]; 
            const found = stats?.chartData?.find(item => item._id === dateStr);
            days.push({
                fullDate: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
                amount: found ? found.dailyRevenue : 0
            });
        }
        return days;
    };
    
    const chartData = getLast7Days();
    const maxGraphVal = Math.max(...chartData.map(d => d.amount), 100); 

    // SVG Line Path Calculation
    const chartWidth = 1000;
    const chartHeight = 250;
    const padding = 40;
    const points = chartData.map((d, i) => ({
        x: (i * (chartWidth / (chartData.length - 1))),
        y: (chartHeight - padding) - (d.amount / maxGraphVal) * (chartHeight - padding * 2)
    }));

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${chartHeight} L 0 ${chartHeight} Z`;

    return (
        <div className="space-y-6">
            
            {/* --- TOP ROW: STATS CARDS --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {statsCards.map((stat, i) => (
                    <div key={i} className="relative overflow-hidden bg-[#1a103c]/40 backdrop-blur-md border border-white/5 p-5 rounded-3xl group transition-all duration-300">
                        <div className={`p-3 w-fit rounded-2xl bg-gradient-to-br ${stat.color} text-white mb-4 shadow-lg flex items-center justify-center`}>
                            {stat.icon}
                        </div>
                        <h3 className="text-3xl font-black text-white tracking-tight">{stat.value}</h3>
                        <p className="text-slate-400 text-sm font-medium mt-1">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* --- MIDDLE ROW: GRAPH & TOP ITEMS --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* 1. ADVANCED NEON LINE GRAPH */}
                <div className="lg:col-span-2 bg-[#1a103c]/60 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] relative shadow-2xl">
                    <div className="flex justify-between items-start mb-10">
                        <div>
                            <h3 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                                <div className="p-2 bg-pink-500/20 rounded-lg"><TrendingUp className="text-pink-500" size={20}/></div>
                                Revenue Analysis
                            </h3>
                            <p className="text-slate-400 text-sm mt-1">Daily trend for the last 7 days</p>
                        </div>
                        <div className="flex gap-2">
                            <div className="px-4 py-2 bg-pink-500/10 border border-pink-500/20 rounded-xl text-pink-400 text-xs font-bold">
                                Weekly View
                            </div>
                        </div>
                    </div>
                    
                    <div className="relative h-72 w-full">
                        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-full overflow-visible">
                            <defs>
                                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#ec4899" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
                                </linearGradient>
                                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur stdDeviation="12" result="blur" />
                                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                </filter>
                            </defs>
                            
                            {/* Grid Lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
                                <line key={i} x1="0" y1={v * chartHeight} x2={chartWidth} y2={v * chartHeight} stroke="rgba(255,255,255,0.03)" strokeWidth="2" />
                            ))}

                            {/* Area Fill */}
                            <path d={areaPath} fill="url(#areaGradient)" className="transition-all duration-1000" />
                            
                            {/* Neon Main Line */}
                            <path 
                                d={linePath} 
                                fill="none" 
                                stroke="#ec4899" 
                                strokeWidth="8" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                filter="url(#glow)"
                                className="transition-all duration-1000"
                            />

                            {/* Dots & Floating Labels */}
                            {points.map((p, i) => (
                                <g key={i} className="group cursor-pointer">
                                    <circle cx={p.x} cy={p.y} r="10" fill="#ec4899" className="animate-pulse" />
                                    <circle cx={p.x} cy={p.y} r="6" fill="#1a103c" />
                                    <foreignObject x={p.x - 35} y={p.y - 45} width="70" height="30">
                                        <div className="bg-white text-[#1a103c] text-[10px] font-black rounded-lg text-center py-1 shadow-xl">
                                            ৳{chartData[i].amount.toFixed(0)}
                                        </div>
                                    </foreignObject>
                                </g>
                            ))}
                        </svg>

                        {/* X-AXIS LABELS */}
                        <div className="flex justify-between mt-8">
                            {chartData.map((day, i) => (
                                <div key={i} className="flex flex-col items-center">
                                    <span className="text-xs font-black text-white uppercase tracking-tighter">{day.dayName}</span>
                                    <span className="text-[10px] font-bold text-slate-500 mt-1">{day.fullDate}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 2. TOP PERFORMANCE (Monthly Best Sellers) */}
                <div className="lg:col-span-1 bg-[#1a103c]/60 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] flex flex-col shadow-2xl">
                    <h3 className="text-xl font-black text-white mb-8 flex items-center gap-2">
                        <div className="w-2 h-6 bg-emerald-500 rounded-full"></div>
                        Monthly Top 5
                    </h3>
                    <div className="space-y-6 overflow-y-auto custom-scrollbar flex-1">
                        {stats?.topSelling?.length > 0 ? (
                            stats.topSelling.map((item, i) => (
                                <div key={i} className="bg-white/5 p-4 rounded-2xl border border-white/5 hover:border-emerald-500/30 transition-all">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-sm font-bold text-white truncate w-32">{item._id}</span>
                                        <span className="text-xs font-black text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-lg">৳{item.totalVal.toFixed(0)}</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-black/40 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full" 
                                            style={{ width: `${(item.count / (stats?.topSelling?.[0]?.count || 1)) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-slate-500 py-10">
                                <Package size={32} className="mx-auto mb-2 opacity-20"/>
                                <p className="text-sm">No sales this month</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- BOTTOM ROW: RECENT ACTIVITY --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8">
                <h3 className="text-xl font-black text-white mb-6">Recent Activity</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-separate border-spacing-y-3">
                        <thead>
                            <tr className="text-slate-500 text-xs font-black uppercase tracking-widest">
                                <th className="px-4">Invoice</th>
                                <th className="px-4">Staff</th>
                                <th className="px-4">Date</th>
                                <th className="px-4 text-right">Revenue</th>
                                <th className="px-4 text-right">Net Profit</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm font-bold">
                            {stats?.recentSales?.map((sale) => (
                                <tr key={sale._id} className="bg-white/5 hover:bg-white/[0.08] transition-colors">
                                    <td className="p-4 rounded-l-2xl text-pink-400 font-mono">#{sale.invoiceNo}</td>
                                    <td className="p-4 text-white">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-pink-500/20 text-[10px] flex items-center justify-center text-pink-500">
                                                {sale.soldBy?.name?.[0] || 'S'}
                                            </div>
                                            {sale.soldBy?.name || 'System'}
                                        </div>
                                    </td>
                                    <td className="p-4 text-slate-400 font-medium">{new Date(sale.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4 text-right text-white">৳{sale.totalAmount.toFixed(2)}</td>
                                    <td className="p-4 rounded-r-2xl text-right text-emerald-400">+৳{(sale.totalProfit || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminHome;